import React, { useEffect, useRef, useState } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Testimonials.css';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const Testimonials = () => {
    const { t } = useTranslation();
    const [currentIndex, setCurrentIndex] = useState(0);
    const sectionRef = useRef(null);
    const titleRef = useRef(null);
    const cardsRef = useRef(null);

    const testimonials = [
        {
            nameKey: 'testimonials.items.testimonial1.name',
            roleKey: 'testimonials.role',
            image: '👩‍⚕️',
            rating: 5,
            textKey: 'testimonials.items.testimonial1.text',
        },
        {
            nameKey: 'testimonials.items.testimonial2.name',
            roleKey: 'testimonials.role',
            image: '👨‍⚕️',
            rating: 5,
            textKey: 'testimonials.items.testimonial2.text',
        },
        {
            nameKey: 'testimonials.items.testimonial3.name',
            roleKey: 'testimonials.role',
            image: '👩‍💼',
            rating: 5,
            textKey: 'testimonials.items.testimonial3.text',
        },
    ];

    useEffect(() => {
        // Animate title
        if (titleRef.current) {
            gsap.fromTo(
                titleRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: 'top 80%',
                        toggleActions: 'play none none none',
                    },
                }
            );
        }

        // Animate cards
        if (cardsRef.current) {
            const cards = cardsRef.current.children;
            Array.from(cards).forEach((card, index) => {
                gsap.fromTo(
                    card,
                    { opacity: 0, y: 60, scale: 0.9 },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        ease: 'power3.out',
                        delay: index * 0.1,
                        scrollTrigger: {
                            trigger: card,
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                        },
                    }
                );
            });
        }

        return () => {
            ScrollTrigger.getAll().forEach((trigger) => {
                if (
                    (sectionRef.current && trigger.vars.trigger === sectionRef.current) ||
                    (titleRef.current && trigger.vars.trigger === titleRef.current)
                ) {
                    trigger.kill();
                }
            });
        };
    }, []);

    const nextTestimonial = () => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    };

    const prevTestimonial = () => {
        setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    };

    return (
        <section id="testimonials" ref={sectionRef} className="testimonials">
            <div className="testimonials-container">
                <div className="testimonials-header">
                    <div className="section-badge">{t('testimonials.badge')}</div>
                    <h2 ref={titleRef} className="section-title">
                        {t('testimonials.title')}
                        <span className="gradient-text"> {t('testimonials.titleHighlight')}</span>
                    </h2>
                    <p className="section-description">
                        {t('testimonials.description')}
                    </p>
                </div>
                <div className="testimonials-carousel">
                    <button className="carousel-btn prev" onClick={prevTestimonial}>
                        <ChevronLeft size={24} />
                    </button>
                    <div ref={cardsRef} className="testimonials-grid">
                        {testimonials.map((testimonial, index) => (
                            <div
                                key={index}
                                className={`testimonial-card ${index === currentIndex ? 'active' : ''}`}
                            >
                                <div className="testimonial-header">
                                    <Quote size={40} className="quote-icon" />
                                    <div className="rating">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} size={20} fill="#fbbf24" color="#fbbf24" />
                                        ))}
                                    </div>
                                </div>
                                <p className="testimonial-text">"{t(testimonial.textKey)}"</p>
                                <div className="testimonial-author">
                                    <div className="author-avatar">{testimonial.image}</div>
                                    <div className="author-info">
                                        <div className="author-name">{t(testimonial.nameKey)}</div>
                                        <div className="author-role">{t(testimonial.roleKey)}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button className="carousel-btn next" onClick={nextTestimonial}>
                        <ChevronRight size={24} />
                    </button>
                </div>
                <div className="carousel-indicators">
                    {testimonials.map((_, index) => (
                        <button
                            key={index}
                            className={`indicator ${index === currentIndex ? 'active' : ''}`}
                            onClick={() => setCurrentIndex(index)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Testimonials;

